import SEO from "@/components/SEO";
import { PageHero, Reveal, Section, Rich, UL, Callout } from "@/components/Bits";
import EnquiryForm from "@/components/EnquiryForm";
import { CONTACT } from "@/content/policies";
import { Smartphone, Mail, Globe2 } from "lucide-react";

export default function DeleteAccount() {
  return (
    <div data-testid="delete-account-page">
      <SEO
        title="Delete Your Account"
        description="Request permanent deletion of your Middle Man account — in-app via Profile → Delete Account, by email, or via this page. Standard requests are completed within 30 days."
        path="/delete-account"
      />
      <PageHero
        kicker="Your data, your choice"
        title="Delete your Middle Man account."
        subtitle={`Middle Man is a brand operated by ${CONTACT.company}, ${CONTACT.address} You can delete your account at any time.`}
      />

      <div className="mx-auto max-w-3xl px-6 py-16 md:py-20">
        <div className="space-y-14">
          <Reveal>
            <Section title="Option 1 — Delete inside the app (fastest)">
              <div className="flex gap-4">
                <Smartphone className="mt-1 h-5 w-5 shrink-0 text-brand" strokeWidth={1.7} />
                <div className="space-y-3">
                  <Rich text="Open the Middle Man app → **Profile → Delete Account** and confirm the two-step prompt. The request is routed to the admin panel for review." />
                  <Rich text="Standard requests are completed within **30 days**." />
                </div>
              </div>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="Option 2 — Request by email">
              <div className="flex gap-4">
                <Mail className="mt-1 h-5 w-5 shrink-0 text-brand" strokeWidth={1.7} />
                <div className="space-y-3">
                  <p>
                    Write to{" "}
                    <a href={`mailto:${CONTACT.email}?subject=ACCOUNT%20DELETION`} data-testid="delete-account-email" className="font-semibold text-brand">
                      {CONTACT.email}
                    </a>{" "}
                    from your registered email with subject <strong className="font-semibold text-ink">"ACCOUNT DELETION — [your registered mobile]"</strong>.
                  </p>
                  <Rich text="Requests submitted by email are handled with the same 30-day timeline." />
                </div>
              </div>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="Option 3 — Request from this website">
              <div className="flex gap-4">
                <Globe2 className="mt-1 h-5 w-5 shrink-0 text-brand" strokeWidth={1.7} />
                <Rich text="Submit the form below. Your request is stored with a reference number and sent to our team immediately. Please mention your **registered mobile number** in the message so we can locate your account." />
              </div>
              <div className="mt-8">
                <EnquiryForm kind="deletion" testid="delete-account-form" buttonLabel="Request account deletion" />
              </div>
            </Section>
          </Reveal>

          <Reveal>
            <Section title="What gets deleted">
              <Rich text="When deletion completes, your account is closed and the personal information tied to it — mobile number, name, email, city, address, profile photo and preferences — is removed from active systems. KYC documents (Aadhaar / PAN and their photos) and trip GPS points are removed with the account, subject only to the legal-retention rules below." />
            </Section>
          </Reveal>

          <Reveal>
            <Section title="What is retained, and for how long">
              <Rich text="As stated in our Privacy Policy (Section 5 — How long we keep information), the following limited retention applies:" />
              <UL items={[
                "Account profile: removed within 30 days of a deletion request, subject to the legal-retention override below.",
                "Work request records: retained for the duration of the engagement plus a reasonable period thereafter to handle disputes, refunds and legal obligations.",
                "Tracking-trail GPS points: capped at 500 points per trip and removed once the trip is closed or when the account is deleted.",
                "KYC documents: removed on account deletion, subject to the legal-retention override.",
                "Payment and subscription records: retained for the period mandated by Indian tax / accounting law (currently up to 8 financial years).",
                "Notification and audit logs: retained for a rolling 12-month window unless required longer for a legal matter.",
                "Legal-retention override: where retention is required by law (for example an open dispute, an unresolved tax audit, or a live court order), the specific record is kept until the legal obligation is discharged, even after a deletion request.",
              ]} />
            </Section>
          </Reveal>

          <Reveal>
            <Callout>
              <Rich text={`Questions about deletion or your data? Contact the Grievance Officer at **${CONTACT.email}** or call **${CONTACT.phone}**. Full details: see our **Privacy Policy** (Sections 5–6).`} />
            </Callout>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
